"use client";

import React, { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetIdentity, useLogout } from "@refinedev/core";
import {
  Layout,
  Menu,
  Avatar,
  Button,
  Dropdown,
  Space,
  Typography,
  theme,
  Switch,
  Drawer,
  Spin,
  Tag,
} from "antd";
import {
  HomeOutlined,
  CalendarOutlined,
  UserOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  SearchOutlined,
  MoonOutlined,
  SunOutlined,
  SolutionOutlined,
  AuditOutlined,
  LogoutOutlined,
  CreditCardOutlined,
  SettingOutlined,
  MailOutlined,
  ScheduleOutlined,
  WalletOutlined,
  PieChartOutlined,
  ReadOutlined,
  CustomerServiceOutlined,
} from "@ant-design/icons";
import { ColorModeContext } from "@contexts/color-mode";
import { StageNoteBrand } from "@components/StageNoteBrand";
import { TopNav } from "./TopNav";
import { useFeature } from "../../hooks/useFeature";
import AppVersion from "../releaseNotes/AppVersion";
import { FEATURE_IDS } from "../../constants/feature-registry";
import { useIsEarlyAccess } from "../../hooks/useIsEarlyAccess";
import { SupportProvider } from "../../contexts/support/SupportContext";

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

interface ModernLayoutProps {
  children: React.ReactNode;
}

export const ModernLayout: React.FC<ModernLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity();
  const { mutate: logout, isLoading: isLoggingOut } = useLogout();
  const { token } = theme.useToken();
  const { enabled: schoolPlannerEnabled } = useFeature(FEATURE_IDS.SCHOOL_ROTATION_PLANNER);
  const { enabled: expensesEnabled } = useFeature(FEATURE_IDS.EXPENSES_TAX_REPORT);
  const { earlyAccess } = useIsEarlyAccess();
  
  const user = identity as any;

  // Check for mobile/tablet screen sizes
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleNavigation = useCallback((path: string) => {
    navigate(path);
    // Close mobile drawer after navigation
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  }, [navigate, isMobile]);

  const toggleMobileDrawer = useCallback(() => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  }, [mobileDrawerOpen]);

  const teachingMenuItems = [
    {
      key: "/dashboard",
      icon: <HomeOutlined />,
      label: "Dashboard",
      onClick: () => handleNavigation("/dashboard"),
    },
    {
      key: "/students",
      icon: <UserOutlined />,
      label: "Students",
      onClick: () => handleNavigation("/students"),
    },
    {
      key: "/calendar",
      icon: <CalendarOutlined />,
      label: "Calendar",
      onClick: () => handleNavigation("/calendar"),
    },
    {
      key: "/locations",
      icon: <EnvironmentOutlined />,
      label: "Locations",
      onClick: () => handleNavigation("/locations"),
    },
    ...(schoolPlannerEnabled ? [{
      key: "/school-planner",
      icon: <ScheduleOutlined />,
      label: "School planner",
      onClick: () => handleNavigation("/school-planner"),
    }] : []),
  ];

  const gigMenuItems = [
    {
      key: "/gig-customers",
      icon: <SolutionOutlined />,
      label: "Gig Customers",
      onClick: () => handleNavigation("/gig-customers"),
    },
    {
      key: "/gig-invoices",
      icon: <AuditOutlined />,
      label: "Gig Invoices",
      onClick: () => handleNavigation("/gig-invoices"),
    },
  ];

  const financeMenuItems = [
    {
      key: "/financial-dashboard",
      icon: <DollarOutlined />,
      label: "Financial Dashboard",
      onClick: () => handleNavigation("/financial-dashboard"),
    },
    {
      key: "/invoices",
      icon: <FileTextOutlined />,
      label: "Invoices",
      onClick: () => handleNavigation("/invoices"),
    },
    ...(expensesEnabled ? [{
      key: "/expenses",
      icon: <WalletOutlined />,
      label: "Expenses",
      onClick: () => handleNavigation("/expenses"),
    }, {
      key: "/year-summary",
      icon: <PieChartOutlined />,
      label: "Year Summary",
      onClick: () => handleNavigation("/year-summary"),
    }] : []),
  ];

  const supportMenuItems = [
    {
      key: "/docs",
      icon: <ReadOutlined />,
      label: "Help & Docs",
      title: "Help & Docs",
      onClick: () => handleNavigation("/docs"),
    },
    {
      key: "/support",
      icon: <CustomerServiceOutlined />,
      label: earlyAccess ? (
        <span>
          Contact support <Tag color="purple" style={{ marginInlineStart: 6 }}>Early access</Tag>
        </span>
      ) : "Contact support",
      title: "Contact support",
      onClick: () => handleNavigation("/support"),
    },
  ];

  const navSections = [
    { key: "teaching", title: "Teaching", items: teachingMenuItems },
    { key: "finance", title: "Finance", items: financeMenuItems },
    { key: "gigs", title: "Gigs", items: gigMenuItems },
    { key: "support", title: null as string | null, items: supportMenuItems },
  ].filter((section) => section.items.length > 0);

  const selectedKey = pathname.startsWith("/support") ? "/support" : (pathname || "/dashboard");

  // Sidebar Content Component
  const SidebarContent = ({ isDrawer = false }) => (
    <div className="app-sidebar" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo Section */}
      <div 
        style={{
          padding: collapsed && !isDrawer ? "12px 4px" : "16px 20px",
          textAlign: collapsed && !isDrawer ? "center" : "left",
          borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
          marginBottom: "8px",
        }}
      >
        {collapsed && !isDrawer ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{
              width: "28px",
              height: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #635bff 0%, #8b5cf6 100%)",
              borderRadius: "6px",
              padding: "4px",
              boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
            }}>
              <img
                src="/logo.svg"
                alt="StageNote Logo"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                style={{ width: "100%", height: "100%", objectFit: "contain", filter: "brightness(0) invert(1)" }}
              />
            </div>
          </div>
        ) : (
          <StageNoteBrand size="md" theme="light" />
        )}
      </div>

      {/* Navigation Menu */}
      <div className="app-sidebar-nav">
        {navSections.map((section) => (
          <div key={section.key} className="app-sidebar-section">
            {section.title && (!collapsed || isDrawer) && (
              <div className="menu-section-header">
                <Text className="section-title">
                  {section.title}
                </Text>
              </div>
            )}
            <Menu
              mode="inline"
              inlineCollapsed={collapsed && !isDrawer}
              selectedKeys={[selectedKey]}
              style={{
                background: "transparent",
                border: "none",
              }}
              items={section.items.map(item => ({
                key: item.key,
                icon: item.icon,
                label: collapsed && !isDrawer ? null : item.label,
                title: "title" in item ? item.title : item.label,
                onClick: item.onClick,
              }))}
            />
          </div>
        ))}
      </div>
      {(!collapsed || isDrawer) && (
        <div className="app-sidebar-footer" style={{ marginTop: 'auto', padding: '12px 16px' }}>
          <AppVersion />
        </div>
      )}
    </div>
  );

  const mobileUserMenuItems = [
    {
      key: "support",
      icon: <CustomerServiceOutlined />,
      label: "Contact support",
      onClick: () => { handleNavigation("/support"); },
    },
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: "Settings",
      onClick: () => { handleNavigation("/settings"); },
    },
    {
      key: "subscription",
      icon: <CreditCardOutlined />,
      label: "Subscription & Billing",
      onClick: () => { handleNavigation("/subscription"); },
    },
    { type: "divider" as const },
    {
      key: "logout",
      icon: isLoggingOut ? <Spin size="small" /> : <LogoutOutlined />,
      label: isLoggingOut ? "Signing out..." : "Sign Out",
      onClick: () => logout(),
      disabled: isLoggingOut,
      danger: true,
    },
  ];

  // Mobile Header Component
  const MobileHeader = () => (
    <div style={{
      background: "#fff",
      padding: "8px 12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      position: "sticky",
      top: 0,
      zIndex: 99,
      borderBottom: "1px solid #e7e7ea",
    }}>
      <Button
        type="text"
        aria-label="Menu"
        icon={<MenuUnfoldOutlined />}
        onClick={toggleMobileDrawer}
        style={{ fontSize: "18px" }}
      />
      <StageNoteBrand size="sm" theme="light" />
      {user && (
        <Dropdown
          menu={{ items: mobileUserMenuItems }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Avatar
            size={32}
            icon={<UserOutlined />}
            style={{
              background: "#eef2ff",
              color: "#4338ca",
              cursor: "pointer",
            }}
          />
        </Dropdown>
      )}
    </div>
  );

  // Get responsive class names
  const getContentClassName = () => {
    if (isMobile) return "mobile-content";
    if (isTablet) return "tablet-content";
    return collapsed ? "laptop-content" : "desktop-content";
  };

  // Check if we should hide mobile header (for lesson show pages)
  const shouldHideMobileHeader = pathname?.includes('/lessons/show/');

  // Mobile Layout
  if (isMobile) {
    return (
      <SupportProvider>
      <Layout style={{ minHeight: "100vh" }}>
        {!shouldHideMobileHeader && <MobileHeader />}
        <Drawer
          title={null}
          placement="left"
          onClose={() => setMobileDrawerOpen(false)}
          open={mobileDrawerOpen}
          closable={false}
          width={280}
          styles={{
            body: {
              padding: 0,
              background: "#f5f5f7",
            },
            header: { display: "none" },
          }}
        >
      <div style={{ background: "#f5f5f7", height: "100%" }}>
        <SidebarContent isDrawer={true} />
      </div>
        </Drawer>
        <Content
          className={getContentClassName()}
          style={{
            background: "#f9fafb",
            minHeight: shouldHideMobileHeader ? "100vh" : "calc(100vh - 56px)",
          }}
        >
          {children}
        </Content>
      </Layout>
      </SupportProvider>
    );
  }

  // Desktop/Tablet Layout
  return (
    <SupportProvider>
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        theme="light"
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={232}
        collapsedWidth={72}
        style={{
          background: "#f5f5f7",
          borderRight: "1px solid #e7e7ea",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
        }}
      >
        <SidebarContent />
      </Sider>

      <Layout style={{ 
        marginLeft: collapsed ? 72 : 232, 
        transition: "all 0.2s" 
      }}>
        <TopNav collapsed={collapsed} />
        <Content
          className={getContentClassName()}
          style={{
            background: "#f9fafb",
            minHeight: "calc(100vh - 64px)",
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
    </SupportProvider>
  );
}; 